import {Component} from '@angular/core';
import {AbstractUnits} from '../abstract-units';
import {StorageSubTechnology, storageSubTechnologyToLabel} from '../../../model/technical/StorageSubTechnology';
import {
  StorageBatteryTechnology,
  storageBatteryTechnologyToLabel
} from '../../../model/technical/StorageBatteryTechnology';
import {StorageBatterySystem, storageBatterySystemToLabel} from '../../../model/technical/StorageBatterySystem';
import {StorageModuleType, storageModuleTypeToLabel} from '../../../model/technical/StorageModuleType';
import {UnitTypeEntry} from '../UnitTypeEntry';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {UnitTypeStorage} from '../../../model/technical/UnitTypeStorage';
import {ProjectDetailsTab} from '../../../../project-details/ProjectDetailsTab';
import {CalcUtil} from '../../../CalcUtil';
import {ReadOnlyReason, ReadOnlyReasonToTooltip} from '../../../../project-details/ReadOnlyStateManager';

@Component({
  selector: 'digires-units-storage',
  templateUrl: './units-storage.component.html',
  styleUrls: ['./units-storage.component.scss']
})
export class UnitsStorageComponent extends AbstractUnits {

  public static createDefaultUnits(
    projectCreation: boolean,
    unitA?: UnitTypeStorage,
    unitB?: UnitTypeStorage,
    unitC?: UnitTypeStorage,
    unitD?: UnitTypeStorage,
  ): UnitTypeEntry[] {
    return UnitsStorageComponent.createDefaultUnitsInternal(
      projectCreation,
      unitA || UnitsStorageComponent.createDefaultUnit('storageA'),
      unitB || UnitsStorageComponent.createDefaultUnit('storageB'),
      unitC || UnitsStorageComponent.createDefaultUnit('storageC'),
      unitD || UnitsStorageComponent.createDefaultUnit('storageD'),
    );
  }

  private static createDefaultUnitsInternal(
    projectCreation: boolean,
    unitA: UnitTypeStorage,
    unitB: UnitTypeStorage,
    unitC: UnitTypeStorage,
    unitD: UnitTypeStorage,
  ): UnitTypeEntry[] {
    return [
      {
        id: 'storageUnitA',
        active: true,
        canAdd: false,
        canDelete: false,
        label: 'Storage Module Type A',
        fg: UnitsStorageComponent.createUnitFg(unitA, projectCreation),
      },
      {
        id: 'storageUnitB',
        active: unitB.active,
        canAdd: false,
        canDelete: false,
        label: 'Storage Module Type B',
        fg: UnitsStorageComponent.createUnitFg(unitB, projectCreation),
      },
      {
        id: 'storageUnitC',
        active: unitC.active,
        canAdd: false,
        canDelete: false,
        label: 'Storage Module Type C',
        fg: UnitsStorageComponent.createUnitFg(unitC, projectCreation),
      },
      {
        id: 'storageUnitD',
        active: unitD.active,
        canAdd: false,
        canDelete: false,
        label: 'Storage Module Type D',
        fg: UnitsStorageComponent.createUnitFg(unitD, projectCreation),
      },
    ];
  }

  private static createUnitFg(unit: UnitTypeStorage, projectCreation: boolean): FormGroup {
    const validatorsSubTech = projectCreation ? [Validators.required] : [];
    return new FormGroup({
      subTechnology: new FormControl(unit.subTechnology, validatorsSubTech),
      areaSize: new FormControl(unit.areaSize),
      description: new FormControl(unit.description),
      batteryTechnology: new FormControl(unit.batteryTechnology),
      batterySystem: new FormControl(unit.batterySystem),
      count: new FormControl(unit.count),
      capacity: new FormControl(unit.capacity),
      totalCapacity: new FormControl(CalcUtil.calculateTotalCapacity(unit.count, unit.capacity, 0.000001)),
      moduleType: new FormControl(unit.moduleType),
      manufacturer: new FormControl(unit.manufacturer),
      inverterType: new FormControl(unit.inverterType),
      inverterCount: new FormControl(unit.inverterCount),
      duration: new FormControl(unit.duration),
    });
  }

  private static createDefaultUnit(id: string): UnitTypeStorage {
    return {
      id: id,
      active: false,
      subTechnology: null,
      manufacturer: null,
      moduleType: null,
      count: null,
      capacity: null,
      areaSize: null,
      description: null,
      batteryTechnology: null,
      batterySystem: null,
      inverterCount: null,
      inverterType: null,
      duration: null,
    };
  }

  readonly allSubTechnologies = Object.values(StorageSubTechnology);
  readonly allBatteryTechnologies = Object.values(StorageBatteryTechnology);
  readonly allBatterySystems = Object.values(StorageBatterySystem);
  readonly allModuleTypes = Object.values(StorageModuleType);
  readonly calculatedReadOnlyReason = ReadOnlyReasonToTooltip[ReadOnlyReason.CALCULATED];

  constructor() {super(ProjectDetailsTab.storage, 0.000001);}

  subTechToLabel(value: StorageSubTechnology): string {
    return storageSubTechnologyToLabel[value];
  }

  batteryTechToLabel(value: StorageBatteryTechnology): string {
    return storageBatteryTechnologyToLabel[value];
  }

  batterySystemToLabel(value: StorageBatterySystem): string {
    return storageBatterySystemToLabel[value];
  }

  moduleTypeToLabel(value: StorageModuleType): string {
    return storageModuleTypeToLabel[value];
  }

}
